//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { useDarkModeContext } from 'contexts/DarkModeContext';
import { OptionType } from 'models/option-type';
import { useEffect, useMemo, useRef, useState } from 'react';
import Select, {
  CSSObjectWithLabel,
  IndicatorSeparatorProps,
  MenuPlacement,
  MenuPosition,
  OptionProps,
  SingleValueProps,
  StylesConfig,
  components,
} from 'react-select';

const CustomOptionDispaly = ({
  data,
  isSelected = false,
  enableSelectedTextEllipsis,
}: {
  data: OptionType;
  isSelected?: boolean;
  enableSelectedTextEllipsis?: boolean;
}) => {
  return (
    <div className="flex justify-between">
      <div className={enableSelectedTextEllipsis ? 'truncate text-start' : 'whitespace-pre-wrap text-start'}>{data.label}</div>
      <div className={`${isSelected ? undefined : 'text-[#AFB1B6]'}`}>{data.customLabel}</div>
    </div>
  );
};

const createCustomOption = (enableSelectedTextEllipsis?: boolean) => (props: OptionProps<OptionType>) => {
  if ('customLabel' in props.data) {
    return (
      <components.Option {...props}>
        <CustomOptionDispaly data={props.data} isSelected={props.isSelected} enableSelectedTextEllipsis={enableSelectedTextEllipsis} />
      </components.Option>
    );
  }
  return (
    <components.Option {...props}>
      <div className={enableSelectedTextEllipsis ? 'truncate text-start' : 'whitespace-pre-wrap text-start'}>{props.data.label}</div>
    </components.Option>
  );
};

const createSingleValue =
  (enableSelectedTextEllipsis?: boolean) =>
  ({ children, ...props }: SingleValueProps<OptionType>) => {
    if (children) {
      return (
        <components.SingleValue {...props}>
          <CustomOptionDispaly data={props.data} enableSelectedTextEllipsis={enableSelectedTextEllipsis} />
        </components.SingleValue>
      );
    }
    return <components.SingleValue {...props}>{children}</components.SingleValue>;
  };

const IndicatorSeparator = ({ innerProps }: IndicatorSeparatorProps<OptionType, true>) => {
  return <span {...innerProps} />;
};

const SingleSelect = ({
  field: { ref, value, onChange, name },
  defaultValue,
  optionList,
  isDisabled,
  sigleSelectClass = '',
  noOptionsMessage,
  hasFormStateError = false,
  menuPlacement = 'bottom',
  customControlStyle = {},
  placeholder = '',
  enableSelectedTextEllipsis = false,
  onBlurCallback,
  isCompact = false,
  isSearchable = true,
  menuPosition = 'absolute',
  menuPortalTarget = undefined,
}: {
  field: any;
  defaultValue: any;
  optionList: readonly OptionType[];
  isDisabled?: boolean;
  sigleSelectClass?: string;
  noOptionsMessage?: any;
  hasFormStateError?: boolean;
  menuPlacement?: MenuPlacement;
  customControlStyle?: CSSObjectWithLabel;
  placeholder?: any;
  enableSelectedTextEllipsis?: boolean;
  onBlurCallback?: () => void;
  isCompact?: boolean; // 緊湊型選單開關 (大小與 padding follow 舊版使用 select的樣式)，依需求決定是否使用
  isSearchable?: boolean; // 是否開啟搜尋功能
  menuPosition?: MenuPosition; // menu 用什麼 css 定位方式
  menuPortalTarget?: HTMLElement | null;
}) => {
  const { isDarkMode } = useDarkModeContext();

  // 建立 Ref 機制處理捲軸與外部點擊
  const [isFocusedState, setIsFocusedState] = useState(false);
  const [isMenuActuallyOpen, setIsMenuActuallyOpen] = useState(false);
  const isMenuActuallyOpenRef = useRef(false);
  const selectContainerRef = useRef<HTMLDivElement>(null);
  // NOTE: 用 ref 包住 onBlurCallback，避免 closure stale 問題，且不需要重新綁定事件監聽
  const onBlurCallbackRef = useRef(onBlurCallback);

  useEffect(() => {
    onBlurCallbackRef.current = onBlurCallback;
  }, [onBlurCallback]);

  useEffect(() => {
    isMenuActuallyOpenRef.current = isMenuActuallyOpen;
  }, [isMenuActuallyOpen]);

  useEffect(() => {
    if (!isMenuActuallyOpen) return;

    const closeMenu = (event: Event) => {
      const target = event.target as HTMLElement;

      // 如果滾動來源是 menu 本身 (選項列表)，不要關閉
      // 用 classNamePrefix 產生的 class 名稱來判斷
      if (target?.closest?.(`[class*="${name}-single-select__menu"]`)) {
        return;
      }
      // 如果滾動來源是 control 本身
      if (selectContainerRef.current?.contains(target)) {
        return;
      }

      setIsMenuActuallyOpen(false);
    };

    document.addEventListener('scroll', closeMenu, true);

    return () => {
      document.removeEventListener('scroll', closeMenu, true);
    };
  }, [isMenuActuallyOpen, name]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // 如果點擊是在組件範圍內，不動作
      if (selectContainerRef.current?.contains(target)) {
        return;
      }
      // 如果點擊是在選單選單內，不動作
      const menuElement = target.closest(`[class*="${name}-single-select__menu"]`);
      if (menuElement) {
        return;
      }

      setIsMenuActuallyOpen(false);
      setIsFocusedState(false);
      onBlurCallbackRef.current?.();
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [name]);

  const selectComponents = useMemo(
    () => ({
      Option: createCustomOption(enableSelectedTextEllipsis),
      SingleValue: createSingleValue(enableSelectedTextEllipsis),
      IndicatorSeparator,
    }),
    [enableSelectedTextEllipsis]
  );

  const primaryColor = isDisabled ? '#D3D3D3' : '#597393';
  const hoverColor = '#C7CCD2';
  // Dark mode colors
  const darkModeColor = 'initial';
  const darkModeMenuColor = '#1F2937';
  const darkModeHover = '#374151';
  const darkModeText = 'unset';
  const darkModeHoverText = '#a1d8e6';
  const stylesConfig: StylesConfig<OptionType, true> = {
    option: (styles, { isFocused, isSelected, isDisabled }) => ({
      ...styles,
      backgroundColor: isDisabled ? undefined : isSelected ? primaryColor : isFocused ? (isDarkMode ? darkModeHover : hoverColor) : undefined,
      cursor: isDisabled ? 'not-allowed' : 'default',
      color: isDisabled ? styles.color : isDarkMode && isFocused ? darkModeHoverText : styles.color,
      ...(isCompact && {
        fontSize: '0.75rem', // text-xs
        fontWeight: 400,
        lineHeight: '19.5px',
      }),
    }),
    valueContainer: (styles) => ({
      ...styles,
      padding: isCompact ? '0px 0px 0px 0.5rem' : '0px 1rem',
      minHeight: 28,
      height: '1.75rem',
      backgroundColor: isDarkMode ? darkModeColor : styles.backgroundColor,
      color: isDarkMode ? darkModeText : styles.color,
      ...(isCompact && { minWidth: 'fit-content' }),
    }),
    control: (styles) => {
      const lightBorderColor = hasFormStateError ? 'hsl(var(--er) / 0.3)' : 'hsl(var(--bc) / 0.3)';
      const lightOutline = isFocusedState ? (hasFormStateError ? '2px solid hsl(var(--er))' : '2px solid hsla(var(--bc) / 0.2)') : 'none';
      const lightOutlineOffset = isFocusedState ? '2px' : '0px';

      const darkBorderColor = hasFormStateError ? 'hsl(var(--er) / 0.3)' : 'hsl(var(--bc) / 0.3)';
      const darkOutline = isFocusedState ? (hasFormStateError ? '2px solid hsl(var(--er))' : '2px solid hsla(var(--bc) / 0.2)') : 'none';
      const darkOutlineOffset = isFocusedState ? '2px' : '0px';

      return {
        ...styles,
        height: '2rem',
        minHeight: 32,
        borderColor: isDarkMode ? darkBorderColor : lightBorderColor,
        boxShadow: 'none',
        outline: isDarkMode ? darkOutline : lightOutline,
        outlineOffset: isDarkMode ? darkOutlineOffset : lightOutlineOffset,
        ...(isCompact && {
          minHeight: 32,
          height: 32,
          borderRadius: '0.25rem',
          minWidth: 'fit-content',
          flexWrap: 'nowrap',
        }),
        ...customControlStyle,
        backgroundColor: isDarkMode ? darkModeColor : styles.backgroundColor,
        color: isDarkMode ? darkModeText : styles.color,
        transition: 'none',
        ':hover': {
          ...styles[':hover'],
          borderColor: isDarkMode ? darkBorderColor : lightBorderColor,
        },
      };
    },
    menu: (styles) => ({
      ...styles,
      backgroundColor: isDarkMode ? darkModeMenuColor : styles.backgroundColor,
      color: isDarkMode ? darkModeText : styles.color,
    }),
    singleValue: (styles) => ({
      ...styles,
      color: isDarkMode ? darkModeText : styles.color,
      ...(enableSelectedTextEllipsis && {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }),
      ...(isCompact && {
        fontSize: '0.75rem', // text-xs
        fontWeight: 600,
        lineHeight: '19.5px',
      }),
    }),
    input: (styles) => ({
      ...styles,
      color: isDarkMode ? darkModeText : styles.color,
    }),
    placeholder: (styles) => ({
      ...styles,
      color: isDarkMode ? '#9CA3AF' : styles.color,
      ...(isCompact && {
        fontSize: '0.75rem', // text-xs
        fontWeight: 600,
      }),
    }),
    indicatorsContainer: (styles) => ({
      ...styles,
      padding: 0,
      ...(isCompact && { height: 32 }),
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      padding: '0 0.25rem',
      color: isFocusedState ? (isDarkMode ? 'white' : '#6b7280') : isDarkMode ? 'rgba(166, 173, 186, 0.5)' : '#cccccc',
      transition: 'color 0.2s',
      ':hover': {
        color: isDarkMode ? 'white' : '#6b7280',
      },
    }),
    indicatorSeparator: (styles) => ({
      ...styles,
      backgroundColor: isDarkMode ? '#6B7280' : styles.backgroundColor,
    }),
    menuPortal: (styles) => ({
      ...styles,
      zIndex: 9999,
    }),
  };

  return (
    <div ref={selectContainerRef} className={sigleSelectClass}>
      <Select
        ref={ref}
        name={name}
        placeholder={placeholder}
        isDisabled={isDisabled}
        options={optionList}
        components={selectComponents}
        menuIsOpen={isMenuActuallyOpen}
        onFocus={() => setIsFocusedState(true)}
        onBlur={() => {
          if (!isMenuActuallyOpenRef.current) setIsFocusedState(false);
        }}
        onMenuOpen={() => setIsMenuActuallyOpen(true)}
        onMenuClose={() => setIsMenuActuallyOpen(false)}
        onChange={(option: any) => {
          onChange(option.value);
        }}
        value={optionList.find((option) => (value !== undefined ? option.value === value : option.value === defaultValue))}
        className="dark:bg-gray-800"
        classNamePrefix={`${name}-single-select`}
        styles={stylesConfig}
        noOptionsMessage={() => noOptionsMessage}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
        menuPortalTarget={menuPortalTarget}
        id={`single-select-for-name-${name}`}
        isSearchable={isSearchable} //避免focus時出現輸入游標
      />
    </div>
  );
};

export default SingleSelect;
